/* eslint-disable react-hooks/exhaustive-deps */
import { ReactElement, createContext, useEffect } from 'react';
import useTenant from 'hooks/useTenant';
import jwtDecode from 'jwt-decode';
import { dispatch, useSelector } from 'store';
import Loader from 'components/Loader';
import { JWTContextType } from 'types/auth';
import axios from 'utils/axios';
import { getItemLocalStorage } from 'hooks/useLocalStorage';
import { getLoggedUsers, setLogin, setLogout } from 'store/reducers/auth';
import { showErrorToast } from 'utils/showErrorToast';
import { RoutesEnum } from 'utils/routesEnum';

const accessToken = getItemLocalStorage('accessToken');

export const isTokenValid = (token: string) => {
  if (!token) {
    return false;
  }

  const decodedToken = jwtDecode(token) as any;
  const expirationTimestamp = decodedToken.exp * 1000;

  const currentTimestamp = Date.now();
  return currentTimestamp < expirationTimestamp;
};

const setSession = (accessToken?: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);
const url = process.env.REACT_APP_API_URL + '/api/user/v1';

export const JWTProvider = ({ children }: { children: ReactElement }) => {
  const { isLoggedIn, isInitialized, user } = useSelector((state) => state.auth);
  const { error: tenantError, isLoading: tenantLoading } = useTenant();

  useEffect(() => {
    if (!window.location.href.includes(RoutesEnum.LOGIN) && !tenantLoading && !tenantError) {
      dispatch(getLoggedUsers());
    }
  }, [accessToken, tenantError, tenantLoading]);

  const login = async (username: string, password: string) => {
    const credentials = `${username}:${password}`;
    const encodedCredentials = window.btoa(credentials);
    const authHeader = `Basic ${encodedCredentials}`;
    const response = await axios.post(
      `${url}${RoutesEnum.LOGIN}`,
      {},
      {
        headers: {
          Authorization: authHeader
        }
      }
    );
    const { access_token } = response.data;
    setSession(access_token);
    dispatch(
      setLogin({
        user: jwtDecode(access_token)
      })
    );
  };

  const logout = () => {
    setSession(null);
    dispatch(setLogout());
    window.location.href = RoutesEnum.LOGIN;
  };

  const resetPassword = async (email: string) => {};

  const updateProfile = () => {};

  useEffect(() => {
    const init = async () => {
      try {
        if (accessToken && isTokenValid(accessToken)) {
          setSession(accessToken);
          dispatch(
            setLogin({
              user: jwtDecode(accessToken)
            })
          );
        } else if (accessToken && !isTokenValid(accessToken)) {
          showErrorToast('Sessão expirada.');
          dispatch(setLogout());
          logout();
        }
      } catch (err) {
        console.error(err);
        dispatch(setLogout());
      }
    };

    init();
  }, []);

  if (isInitialized !== undefined && !isInitialized && user) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider value={{ user, isLoggedIn, isInitialized, login, logout, resetPassword, updateProfile }}>
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
